
import { ASKED_TO_UNMUTE_SOUND_ID } from '../../react/features/av-moderation/constants';
import { JitsiConferenceEvents } from '../../react/features/base/lib-jitsi-meet';
import { playSound } from '../../react/features/base/sounds/actions';
import {
    NOTIFICATION_TIMEOUT_TYPE,
    NOTIFICATION_TYPE
} from '../../react/features/notifications/constants';
import React from 'react';
import {
    HintsNotification,
    setHints
} from '../components/hints';
import {
    ACCUMULATE_DATA_TIMEOUT,
    INTERVALS
} from '../components/hints/constants';
import {
    isAllowed,
    isEnabled,
    isValidRatio
} from '../components/hints/functions';
import { isAlone } from '../components/popup-alone/functions';
import WildixEvents from '../service/WildixEvents';

export default class SpeakersInfo {

    _intervalIndex = 0;
    _intervals = INTERVALS;
    _showStatisticTimeout = ACCUMULATE_DATA_TIMEOUT;
    _intervalId = null;

    currentDominantSpeakerId = null;
    _dominantSpeakerStart = {};
    _totalConferenceTime = 0;

    constructor() {
        document.addEventListener(WildixEvents.CONFERENCE_JOINED, () => {
            this.setData(APP.wildix.storage.getHintsSettings());
            APP.conference.__getJitsiConference().addEventListener(
                JitsiConferenceEvents.DOMINANT_SPEAKER_CHANGED,
                e => {
                    if (isAllowed()) {
                        this._onDominantSpeaker(e);
                    }
                });
        });
    }

    clear(enabled) {
        if (!this._intervalId || enabled) {
            return;
        }

        clearTimeout(this._intervalId);
        this._intervalId = null;
    }

    setDominantSpeaker(speakerId, isNowDominantSpeaker, data) {
        if (!this.hasDominantSpeakerJustSpoke(speakerId) && isNowDominantSpeaker) {
            this._dominantSpeakerStart[speakerId] = Date.now();
        } else if (this.hasDominantSpeakerJustSpoke(speakerId) && !isNowDominantSpeaker) {
            const now = Date.now();
            const timeElapsed = now - this._dominantSpeakerStart[speakerId];

            // check if timeElapsed is a longest monolog than ever
            if (data[speakerId] && (!data[speakerId].longestMonolog
                || data[speakerId].longestMonolog < timeElapsed)) {
                data[speakerId].longestMonolog = timeElapsed;
            }

            this._dominantSpeakerStart[speakerId] = 0;
        }
    }

    hasDominantSpeakerJustSpoke(speakerId) {
        return this._dominantSpeakerStart[speakerId] > 0;
    }

    _onDominantSpeaker(speakerId) {
        const stats = APP.conference.getSpeakerStats();
        const arr = Object.values(stats);
        const data = { ...APP.store.getState()['wildix/features/hints'].data };

        // calc total conference duration
        this._totalConferenceTime = arr
            .reduce((total, i) => total + i.totalDominantSpeakerTime, 0);

        arr.forEach(i => {
            // check if user has incorrect id
            if (i._userId.includes('admin_')) {
                return;
            }

            // check if user not exist
            if (!data[i._userId]) {
                data[i._userId] = {};
            }

            const user = data[i._userId];

            user.displayName = this.getDisplayName(i);
            user.ratio = this.getRatio(i);
        });

        const oldDominantSpeaker
            = stats[this.currentDominantSpeakerId];
        const newDominantSpeaker = stats[speakerId];

        oldDominantSpeaker && this.setDominantSpeaker(this.currentDominantSpeakerId, false, data);
        newDominantSpeaker && this.setDominantSpeaker(speakerId, true, data);

        // update data in store
        this.setData({
            data,
            currentUser: data[APP.conference.getMyUserId()]
        });

        // update current ID
        this.currentDominantSpeakerId = speakerId;

        // 1st notification if we crossed the threshold
        if (this.canRunIntervalWithNotification()) {
            this.showNotification();
            this.startInterval();
        }
    }

    isDataAccumulated() {
        return this._totalConferenceTime > this._showStatisticTimeout;
    }

    canRunIntervalWithNotification() {
        return Boolean(!this._intervalId
            && !isAlone()
            && isEnabled()
            && this.isDataAccumulated()
            && !isValidRatio());
    }

    startInterval() {
        const interval = this._intervals[this._intervalIndex];

        this._intervalId = setTimeout(() => {
            if (!isValidRatio()) {
                this.showNotification();
            }
            this.startInterval();
        }, interval);

        if (this._intervalIndex >= (this._intervals.length - 1)) {
            this._intervalIndex = this._intervals.length - 1;
        } else {
            this._intervalIndex += 1;
        }
    }

    showNotification() {
        const state = APP.store.getState();
        const { currentUser, enabledSoundHints } = state['wildix/features/hints'];
        const props = {
            key: Date.now(),
            user: currentUser
        };

        if (enabledSoundHints) {
            APP.store.dispatch(playSound(ASKED_TO_UNMUTE_SOUND_ID));
        }

        APP.wildix.eventEmitter.emit(WildixEvents.SHOW_NOTIFICATION, {
            appearance: NOTIFICATION_TYPE.WARNING,
            titleKey: 'speakerStats.yourMetrics',
            description: <HintsNotification {...props} />
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM);
    }

    setData(payload) {
        APP.store.dispatch(setHints(payload));
    }

    getDisplayName(user) {
        if (user.displayName || !user._isLocalStats) {
            return user.displayName;
        }

        const { displayName } = APP.store.getState()['features/base/settings'];
        const meString = APP.wildix.methods.translate('me');

        return `${displayName} (${meString})`;
    }

    getRatio(user) {
        return user.totalDominantSpeakerTime > 0
            ? Math.round(user.totalDominantSpeakerTime / this._totalConferenceTime * 100) : 0;
    }
}
